import React, { useMemo, useState } from "react";
import { Box, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import FeriaMaterialCard from "./FeriaMaterialCard";
import {
  clearFeriaInboxPhotoDrag,
  isFeriaPhotoDragEvent,
  readFeriaInboxPhotoDragIds,
} from "./feriaPhotoDrag";
import {
  materialBelongsToSupplier,
  supplierDisplayName,
  unassignedFeriaMaterials,
} from "./feriaMaterialBelong";

const ACCENT = "#ffac7e";

/**
 * Centro: grupos de proveedor en pastillas (grid horizontal).
 * Las fotos pendientes van en el dock inferior (sesión).
 */
const FeriaSupplierMaterialsWorkspace = ({
  proveedores = [],
  materiales = [],
  focusSupplierId = null,
  highlightMaterialId = null,
  dragHoverSupplierId = null,
  onDesktopFilesDropOnSupplier,
  onDropSessionPhotos,
  onEditMaterial,
  onDeleteMaterial,
  onEstadoChange,
  onAssignClick,
  onThumbClick,
  assignMenu = { anchor: null, feriaMaterialId: null },
  onAssignMenuClose,
  onAssignToSupplier,
  showUnassigned = false,
}) => {
  const theme = useTheme();
  const [dropActiveId, setDropActiveId] = useState(null);

  const unassigned = useMemo(
    () => unassignedFeriaMaterials(materiales, proveedores),
    [materiales, proveedores],
  );

  if (!proveedores.length && !(showUnassigned && unassigned.length)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Añade un proveedor a la derecha y arrastra fotos encima para crear
        materiales aquí.
      </Typography>
    );
  }

  const renderPills = (mats, withAssign) => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.25,
        alignContent: "flex-start",
      }}
    >
      {mats.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 0.5, py: 1 }}
        >
          Sin materiales — arrastra fotos al proveedor.
        </Typography>
      ) : (
        mats.map((m) => (
          <FeriaMaterialCard
            key={m.id_FeriaMaterial}
            row={m}
            variant="pill"
            highlighted={
              Number(highlightMaterialId) === Number(m.id_FeriaMaterial)
            }
            showAssignChip={withAssign}
            assignLabel="Mover"
            onEdit={onEditMaterial}
            onDelete={onDeleteMaterial}
            onEstadoChange={onEstadoChange}
            onAssignClick={onAssignClick}
            onThumbClick={onThumbClick}
          />
        ))
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.75,
          alignItems: "stretch",
        }}
      >
        {(proveedores || []).map((row) => {
          const id = Number(row.id_FeriaProveedor);
          const mats = (materiales || []).filter((m) =>
            materialBelongsToSupplier(m, row),
          );
          const name = supplierDisplayName(row);
          const focused = Number(focusSupplierId) === id;
          const isTrader = Boolean(row.proveedor);
          const dropActive =
            Number(dropActiveId) === id || Number(dragHoverSupplierId) === id;

          const acceptDrop = (e) => {
            const types = [...(e.dataTransfer?.types || [])];
            if (!types.includes("Files") && !isFeriaPhotoDragEvent(e)) {
              return false;
            }
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "copy";
            setDropActiveId(id);
            return true;
          };

          return (
            <Box
              key={id}
              id={`feria-center-supplier-${id}`}
              data-feria-supplier-drop={id}
              onDragEnter={acceptDrop}
              onDragOver={acceptDrop}
              onDragLeave={() => {
                setDropActiveId((cur) => (Number(cur) === id ? null : cur));
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropActiveId(null);
                const photoIds = readFeriaInboxPhotoDragIds(e);
                clearFeriaInboxPhotoDrag();
                if (photoIds.length && onDropSessionPhotos) {
                  await onDropSessionPhotos(id, photoIds);
                  return;
                }
                const files = Array.from(e.dataTransfer?.files || []).filter(
                  (f) =>
                    String(f.type || "")
                      .toLowerCase()
                      .startsWith("image/"),
                );
                if (files.length && onDesktopFilesDropOnSupplier) {
                  await onDesktopFilesDropOnSupplier(row, files);
                }
              }}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 10px)",
                  lg: "1 1 calc(33.333% - 12px)",
                },
                minWidth: { xs: "100%", sm: 260 },
                maxWidth: { lg: "calc(50% - 10px)" },
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: dropActive
                  ? alpha(theme.palette.primary.main, 0.1)
                  : "#fff",
                border: "2px solid",
                borderColor: dropActive
                  ? theme.palette.primary.main
                  : focused
                    ? alpha(theme.palette.primary.main, 0.55)
                    : "transparent",
                boxShadow: dropActive
                  ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`
                  : focused
                    ? `0 2px 10px ${alpha("#0b1a24", 0.08)}`
                    : `0 1px 3px ${alpha("#0b1a24", 0.06)}`,
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.25 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(
                      isTrader ? theme.palette.primary.main : ACCENT,
                      0.14,
                    ),
                    color: isTrader ? theme.palette.primary.main : "#c9784a",
                    flexShrink: 0,
                  }}
                >
                  {isTrader ? (
                    <StorefrontOutlinedIcon sx={{ fontSize: 17 }} />
                  ) : (
                    <BusinessOutlinedIcon sx={{ fontSize: 17 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontWeight: 800, fontSize: "0.88rem" }}
                  >
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dropActive
                      ? "Suelta aquí"
                      : `${isTrader ? "Trader" : "Mill"} · ${mats.length}`}
                  </Typography>
                </Box>
              </Stack>
              {renderPills(mats, true)}
            </Box>
          );
        })}
      </Box>

      {showUnassigned && unassigned.length > 0 ? (
        <Box
          sx={{
            mt: 1.75,
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: "#fff",
            outline: `1px dashed ${alpha(theme.palette.warning.main, 0.55)}`,
            boxShadow: `0 1px 3px ${alpha("#0b1a24", 0.06)}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", mb: 1 }}>
            Sin proveedor · {unassigned.length}
          </Typography>
          {renderPills(unassigned, true)}
        </Box>
      ) : null}

      <Menu
        anchorEl={assignMenu.anchor}
        open={Boolean(assignMenu.anchor)}
        onClose={onAssignMenuClose}
      >
        <MenuItem
          disabled
          sx={{ opacity: 1, fontWeight: 700, fontSize: "0.8rem" }}
        >
          Asignar a proveedor
        </MenuItem>
        <MenuItem
          onClick={() => onAssignToSupplier?.(assignMenu.feriaMaterialId, null)}
        >
          Sin proveedor
        </MenuItem>
        {(proveedores || []).map((row) => (
          <MenuItem
            key={row.id_FeriaProveedor}
            onClick={() =>
              onAssignToSupplier?.(assignMenu.feriaMaterialId, row)
            }
          >
            {supplierDisplayName(row)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default FeriaSupplierMaterialsWorkspace;
